import { Extension } from "@tiptap/core";

// Extensoes de formatacao do editor de documentos. Tudo e gravado como CSS em
// linha porque o mesmo HTML vai para o PDF, o Word e o e-mail, onde o CSS do
// editor nao chega.

const BLOCK_TYPES = ["paragraph", "heading"];

// Recuo padrao do Word: 1,25 cm por nivel.
export const INDENT_STEP_CM = 1.25;
const MAX_INDENT_CM = 12.5;

function toCm(value) {
    if (value === null || value === undefined || value === "") return 0;
    const text = String(value).trim();
    const number = parseFloat(text);
    if (Number.isNaN(number)) return 0;
    if (text.endsWith("cm")) return number;
    if (text.endsWith("mm")) return number / 10;
    if (text.endsWith("pt")) return number / 28.3465;
    if (text.endsWith("in")) return number * 2.54;
    if (text.endsWith("em")) return (number * 16) / 37.795;
    return number / 37.795; // px
}

function cm(value) {
    const rounded = Math.round(value * 100) / 100;
    return rounded ? `${rounded}cm` : null;
}

// Atributos que leem e gravam propriedades CSS em linha: { nome: "propriedade-css" }.
function styleAttributes(map) {
    const result = {};
    Object.entries(map).forEach(([name, cssProperty]) => {
        result[name] = {
            default: null,
            parseHTML: (element) => {
                const value = element.style.getPropertyValue(cssProperty) || null;
                return cssProperty === "color" ? toHex(value) : value;
            },
            renderHTML: (attributes) => (attributes[name] ? { style: `${cssProperty}: ${attributes[name]}` } : {}),
        };
    });
    return result;
}

// Tamanho da fonte em pt (marca textStyle).
export const FontSize = Extension.create({
    name: "fontSize",

    addGlobalAttributes() {
        return [
            {
                types: ["textStyle"],
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (element) => element.style.fontSize || null,
                        renderHTML: (attributes) => (attributes.fontSize ? { style: `font-size: ${attributes.fontSize}` } : {}),
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setFontSize: (size) => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
            unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
        };
    },
});

// Entrelinha, recuo esquerdo, recuo de primeira linha e estilo nomeado
// (data-style, usado pelo painel de estilos) nos paragrafos e titulos.
export const ParagraphFormat = Extension.create({
    name: "paragraphFormat",

    addGlobalAttributes() {
        return [
            {
                types: BLOCK_TYPES,
                attributes: {
                    lineHeight: {
                        default: null,
                        parseHTML: (element) => element.style.lineHeight || null,
                        renderHTML: (attributes) => (attributes.lineHeight ? { style: `line-height: ${attributes.lineHeight}` } : {}),
                    },
                    indentLeft: {
                        default: null,
                        parseHTML: (element) => cm(toCm(element.style.marginLeft)),
                        renderHTML: (attributes) => (attributes.indentLeft ? { style: `margin-left: ${attributes.indentLeft}` } : {}),
                    },
                    textIndent: {
                        default: null,
                        parseHTML: (element) => cm(toCm(element.style.textIndent)),
                        renderHTML: (attributes) => (attributes.textIndent ? { style: `text-indent: ${attributes.textIndent}` } : {}),
                    },
                    dataStyle: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("data-style"),
                        renderHTML: (attributes) => (attributes.dataStyle ? { "data-style": attributes.dataStyle } : {}),
                    },
                    ...styleAttributes({
                        fontFamily: "font-family",
                        fontSize: "font-size",
                        fontWeight: "font-weight",
                        fontStyle: "font-style",
                        textTransform: "text-transform",
                        color: "color",
                        spaceBefore: "margin-top",
                        spaceAfter: "margin-bottom",
                    }),
                },
            },
        ];
    },

    addCommands() {
        const updateBlocks = (updater) => ({ tr, state, dispatch }) => {
            const { from, to } = state.selection;
            let changed = false;
            state.doc.nodesBetween(from, to, (node, pos) => {
                if (!BLOCK_TYPES.includes(node.type.name)) return;
                const attrs = updater(node.attrs);
                if (!attrs) return;
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs });
                changed = true;
            });
            if (changed && dispatch) dispatch(tr);
            return changed;
        };

        return {
            setLineHeight: (lineHeight) => updateBlocks(() => ({ lineHeight })),
            indent: () => updateBlocks((attrs) => ({ indentLeft: cm(Math.min(MAX_INDENT_CM, toCm(attrs.indentLeft) + INDENT_STEP_CM)) })),
            outdent: () => updateBlocks((attrs) => ({ indentLeft: cm(Math.max(0, toCm(attrs.indentLeft) - INDENT_STEP_CM)) })),
            // Valores em cm vindos da regua (null = manter).
            setIndents: ({ left = null, firstLine = null }) => updateBlocks(() => {
                const attrs = {};
                if (left !== null) attrs.indentLeft = cm(Math.max(0, left));
                if (firstLine !== null) attrs.textIndent = cm(firstLine);
                return attrs;
            }),
        };
    },

    addKeyboardShortcuts() {
        return {
            // Tab fora de lista e tabela aumenta o recuo, como no Word.
            Tab: () => {
                if (this.editor.isActive("listItem") || this.editor.isActive("table")) return false;
                return this.editor.commands.indent();
            },
            "Shift-Tab": () => {
                if (this.editor.isActive("listItem") || this.editor.isActive("table")) return false;
                return this.editor.commands.outdent();
            },
        };
    },
});

// rgb(21, 0, 48) -> #150030 (o navegador devolve cores em rgb).
export function toHex(value) {
    if (!value) return null;
    const match = String(value).match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!match) return value;
    return `#${match.slice(1, 4).map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`;
}

// Estilo de trecho (marca textStyle): peso, italico, caixa alta e o nome do
// estilo aplicado (data-char-style). Fonte, tamanho e cor ja vem das
// extensoes FontFamily, FontSize e Color.
export const CharacterFormat = Extension.create({
    name: "characterFormat",

    addGlobalAttributes() {
        return [
            {
                types: ["textStyle"],
                attributes: {
                    ...styleAttributes({ fontWeight: "font-weight", fontStyle: "font-style", textTransform: "text-transform" }),
                    charStyle: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("data-char-style"),
                        renderHTML: (attributes) => (attributes.charStyle ? { "data-char-style": attributes.charStyle } : {}),
                    },
                },
            },
        ];
    },
});

export { toCm, cm };
